import { ICorporationResponse } from '@kcftech/prometheustypescriptapiclient';
import { Dropdown, DropdownItemRenderer } from 'components/dropdown/Dropdown';
import {
    onDropdownItemLinkClick,
    SimpleDropdownLabelRenderer,
    useDropdownLabelRenderer,
} from 'components/dropdown/shared';
import { DropdownItemLink } from 'components/dropdown/styled';
import {
    infiniteCorporationsDataSourceKey,
    useInfiniteCorporationDataSource,
} from 'custom_hooks/data_hooks/data_source/corporationInfiniteDataSource';
import { useCallback } from 'react';
import { getId } from 'utils/data';
import { getCorporationLandingPageRoute } from 'utils/RouteHelper';

export type CorporationDropdownProps = Readonly<{
    /**
     * The currently selected corporation.
     */
    value: ICorporationResponse | undefined;
    /**
     * Callback to call when the selected corporation should change.
     */
    onChange: (corporation: ICorporationResponse) => void;
    /**
     * Placeholder text for the search field.
     */
    placeholder?: string;
    className?: string;
    popupWidth?: string;
    renderLabel?: SimpleDropdownLabelRenderer<ICorporationResponse>;
    interactionMode?: 'link' | 'select';
}>;

export const CorporationAbbreviation: SimpleDropdownLabelRenderer<ICorporationResponse> = ({
    value: selectedCorp,
}) => selectedCorp.Abbreviation ?? selectedCorp.Name.substring(0, 5);

const renderItem: DropdownItemRenderer<ICorporationResponse> = ({ item }) => (
    <DropdownItemLink
        onClick={onDropdownItemLinkClick}
        to={getCorporationLandingPageRoute(item.Id)}
    >
        {item.Name}
    </DropdownItemLink>
);

const renderItemNoLink: DropdownItemRenderer<ICorporationResponse> = ({ item }) => (
    <div>{item.Name}</div>
);

/**
 * Dropdown which allows selecting a corporation from a list of available locations.
 */
export function CorporationDropdown({
    value,
    onChange,
    placeholder = 'Select a Corporation',
    className,
    popupWidth,
    renderLabel: renderLabelProp,
    interactionMode = 'link',
}: CorporationDropdownProps) {
    const renderLabel = useDropdownLabelRenderer({
        defaultRenderer: (info) => info.value.Name,
        overrideRenderer: renderLabelProp,
        placeholder,
    });

    const {
        isLoading,
        hasNext,
        loadNext,
        results,
        filter,
        setFilter,
    } = useInfiniteCorporationDataSource({
        name: infiniteCorporationsDataSourceKey,
        defaultFilter: {},
    });

    const setSearchText = useCallback(
        (searchText: string) => {
            setFilter((currentFilter) => ({
                ...currentFilter,
                nameSearch: searchText,
            }));
        },
        [setFilter]
    );

    return (
        <Dropdown<ICorporationResponse>
            data={{
                items: results,
                isLoading,
                hasMore: hasNext ?? false,
                onLoadMore: loadNext,
            }}
            dataKey={getId}
            renderLabel={renderLabel}
            renderItem={interactionMode === 'link' ? renderItem : renderItemNoLink}
            value={value}
            onChange={onChange}
            filterable={{
                filterText: filter.nameSearch ?? '',
                onChange: setSearchText,
                placeholder: 'Search Corporations',
            }}
            trigger={{ className }}
            popup={{ width: popupWidth }}
        />
    );
}
